import React from "react";
import SectionHeader from "../SectionHeader";
import {
  IconUserPlus,
  IconIdCard,
  IconLink,
  IconHandHeart,
  IconVerified,
  IconShield,
  IconArrowLeft,
  IconMore,
  IconBell,
  IconSearch,
  IconReply,
  IconHome,
  IconUsers,
  IconChat,
  IconUser,
} from "../brand/icons";

const steps = [
  {
    n: 1,
    icon: IconUserPlus,
    title: "Apply to Join",
    text: "Complete our verification process",
    foot: "Quick 5-min review",
  },
  {
    n: 2,
    icon: IconIdCard,
    title: "Create Your Profile",
    text: "Showcase your expertise and approach",
    foot: "Highlight specializations",
  },
  {
    n: 3,
    icon: IconLink,
    title: "Connect with Families",
    text: "Engage with those seeking support",
    foot: "Direct family matching",
  },
  {
    n: 4,
    icon: IconHandHeart,
    title: "Make an Impact",
    text: "Help build stronger communities",
    foot: "Meaningful lived change",
  },
];

const StepCard = ({ step }) => {
  const Icon = step.icon;
  return (
    <div className="p-6 rounded-2xl bg-surface-white border border-hairline shadow-comfort text-left flex flex-col">
      <div>
        <div className="flex items-center justify-between mb-4">
          <span className="w-9 h-9 rounded-full bg-cloud-mist text-spectrum-blue font-extrabold flex items-center justify-center text-label-md">
            {step.n}
          </span>
          <Icon size={26} className="text-sky-blue" />
        </div>
        <h3 className="font-headline text-headline-sm font-bold text-ink mb-2">
          {step.title}
        </h3>
        <p className="font-body text-body-md text-muted">{step.text}</p>
      </div>
      <div className="mt-auto pt-5 border-t border-cloud-mist">
        <span className="inline-flex items-center rounded-brand bg-cloud-mist px-3 py-1.5 text-label-sm font-semibold text-spectrum-blue">
          {step.foot}
        </span>
      </div>
    </div>
  );
};

const PhoneFrame = ({ children, className = "" }) => (
  <div
    className={`w-[190px] sm:w-[230px] lg:w-[260px] h-[400px] sm:h-[480px] lg:h-[530px] bg-ink rounded-[36px] sm:rounded-[40px] p-2.5 sm:p-3 shadow-phone relative border-4 border-outline-variant shrink-0 ${className}`}
    aria-hidden="true"
  >
    <div className="absolute top-3.5 left-1/2 -translate-x-1/2 w-20 h-3.5 bg-ink rounded-full z-30" />
    <div className="w-full h-full bg-surface-white rounded-[26px] sm:rounded-[30px] overflow-hidden flex flex-col pt-8 sm:pt-7 px-3 sm:px-3.5 pb-3">
      {children}
    </div>
  </div>
);

const AllyProfilePhone = () => (
  <PhoneFrame>
    <div className="flex items-center justify-between pb-3 border-b border-cloud-mist">
      <IconArrowLeft size={15} className="text-spectrum-blue" />
      <span className="text-xs font-bold text-spectrum-blue">Ally Profile</span>
      <IconMore size={15} className="text-spectrum-blue" />
    </div>

    <div className="mt-3 flex flex-col items-center">
      <div className="w-16 h-16 rounded-full bg-cloud-mist border-2 border-spectrum-blue flex items-center justify-center text-spectrum-blue font-extrabold text-lg">
        SC
      </div>
      <div className="mt-2 text-center">
        <div className="flex items-center justify-center gap-1">
          <span className="text-xs font-bold text-ink">Sarah Chen</span>
          <IconVerified size={13} className="text-heart-teal" />
        </div>
        <span className="text-[10px] text-muted block">
          Paediatric Occupational Therapist
        </span>
      </div>
      <div className="mt-2 flex gap-1">
        <span className="bg-sunshine-soft text-ink px-2 py-0.5 rounded-full text-[9px] font-semibold">
          Sensory Integration
        </span>
        <span className="bg-cloud-mist text-spectrum-blue px-2 py-0.5 rounded-full text-[9px] font-semibold">
          Neuro-affirming
        </span>
      </div>
    </div>

    <div className="mt-4 bg-mist-soft p-2.5 rounded-brand border border-cloud-mist text-left">
      <span className="text-[9px] uppercase font-bold text-sky-blue block mb-1">
        My Approach
      </span>
      <p className="text-[10px] text-muted leading-snug">
        Supporting regulation &amp; autonomous self-advocacy through
        strength-based parent coaching.
      </p>
    </div>

    <div className="mt-3 bg-teal-tint border border-teal-soft p-2 rounded-brand text-left flex items-center gap-2">
      <IconShield size={15} className="text-heart-teal" />
      <span className="text-[9px] font-bold text-ink">NDIS Registered Ally</span>
    </div>

    <div className="mt-auto">
      <div className="w-full bg-spectrum-blue text-surface-white py-2 rounded-brand text-[10px] font-bold text-center">
        Message Dr. Chen
      </div>
    </div>
  </PhoneFrame>
);

const VillageForumPhone = () => (
  <PhoneFrame className="hidden sm:block">
    <div className="flex items-center justify-between pb-3 border-b border-cloud-mist">
      <span className="text-xs font-bold text-spectrum-blue">Village Forum</span>
      <IconBell size={15} className="text-spectrum-blue" />
    </div>

    <div className="mt-2.5 bg-mist-soft py-1.5 px-2 rounded-brand border border-cloud-mist flex items-center gap-1">
      <IconSearch size={12} className="text-outline-variant" />
      <span className="text-[9px] text-muted">Search topics or ally threads...</span>
    </div>

    <div className="mt-3 bg-surface-white p-2.5 rounded-brand border border-cloud-mist text-left shadow-brand-sm">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-5 h-5 rounded-full bg-cloud-mist text-[8px] font-bold flex items-center justify-center text-spectrum-blue">
          ML
        </div>
        <span className="text-[9px] font-bold text-ink">Maya (Mum to Leo, 6)</span>
      </div>
      <p className="text-[9px] text-muted font-medium">
        Looking for advice transitioning to prep school next term?
      </p>
      <div className="mt-1.5 bg-mist-chip p-1.5 rounded-lg flex items-center gap-1.5">
        <IconReply size={12} className="text-sky-blue" />
        <span className="text-[8px] text-spectrum-blue font-bold">
          2 Allies responded
        </span>
      </div>
    </div>

    <div className="mt-2.5 bg-surface-white p-2.5 rounded-brand border border-cloud-mist text-left shadow-brand-sm">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-5 h-5 rounded-full bg-sunshine text-[8px] font-bold flex items-center justify-center text-ink">
          DS
        </div>
        <span className="text-[9px] font-bold text-ink">David S. (Speech Ally)</span>
      </div>
      <p className="text-[9px] text-muted font-medium">
        Shared: &ldquo;Visual schedules for smooth evening wind-downs&rdquo;
      </p>
      <div className="mt-1.5 flex items-center gap-2 text-[8px] text-muted">
        <span>42 parents</span>
        <span>16 comments</span>
      </div>
    </div>

    <div className="mt-auto pt-2 border-t border-cloud-mist flex justify-around">
      <IconHome size={14} className="text-spectrum-blue" />
      <IconUsers size={14} className="text-outline-variant" />
      <IconChat size={14} className="text-outline-variant" />
      <IconUser size={14} className="text-outline-variant" />
    </div>
  </PhoneFrame>
);

const Becomeanally2 = () => {
  return (
    <section className="w-full py-16 md:py-24 bg-surface-white text-left">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
        <SectionHeader
          title="Simple Onboarding"
          light="How to become an"
          bold="Ally"
          para="A thoughtful, affirming path designed to respect your time and preserve our community's safety and integrity."
          className="mb-12 md:mb-16 max-w-3xl"
        />

        <div className="grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-3 order-2 lg:order-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 lg:grid-rows-2 gap-6">
            {steps.slice(0, 2).map((step) => (
              <StepCard key={step.n} step={step} />
            ))}
          </div>

          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2 flex justify-center items-center gap-4 sm:gap-6">
            <AllyProfilePhone />
            <VillageForumPhone />
          </div>

          <div className="col-span-12 lg:col-span-3 order-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 lg:grid-rows-2 gap-6">
            {steps.slice(2).map((step) => (
              <StepCard key={step.n} step={step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Becomeanally2;
